import { Card } from "@/src/components/Card";
import { DetailedFileHistoryModal } from "@/src/components/data_room/DetailedFileHistoryModal";
import {
  DataRoomFileVersion,
  DataRoomFileVersionsQuery,
  DealRoleType,
  FileType,
  FileVersionTextChange,
  PermissionsResourceType,
  useDataRoomFileVersionsQuery,
  useUpdateDataRoomFileMutation,
} from "@/src/graphql/generated";
import { useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { DeleteVersionModal } from "./DeleteVersionModal";
import { RedlineExportModal } from "./RedlineExportModal";
import { useDispatch, useSelector } from "react-redux";
import { dataRoomFileSelectors } from "@/src/store/dataRoomFile/selector";
import { AppState } from "@/src/store";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { classNames } from "@/src/utils/cn";
import { actions } from "@/src/store/dataRoomFile/slice";
import { Avatar } from "@/src/components/account/Avatar";
import { formatDistance, formatISO, secondsToMilliseconds } from "date-fns";
import { FilePill } from "@/src/components/FilePill";
import { TripleDotMenu } from "@/src/components/TripleDotMenu";
import { MenuItem } from "@headlessui/react";
import { toasts } from "@/src/components/toasts/toasts";
import { FeatureFlag } from "@/src/components/posthog/FeatureFlag";
import { TextDiffs } from "@/src/components/tailwind/TextDiffs";
import React from "react";
import Dropdown, { Option } from "@/src/components/tailwind/Dropdown";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import Loading from "@/src/components/Loading";

type VersionsFile = DataRoomFileVersionsQuery["dataRoomFile"];
type VersionItem = VersionsFile["versions"][0];

export function VersionHistory(props: { fileId: string }) {
  const client = useGqlClient();
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState<string>("");
  const [deleteVersion, setDeleteVersion] = useState<string>("");
  const [redlineExport, setRedlineExport] = useState<{
    sourceId: string;
    targetId: string;
  } | null>(null);

  const versionsQuery = useDataRoomFileVersionsQuery(
    client,
    { id: props.fileId },
    {
      enabled: !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (versionsQuery.isPending || !versionsQuery.data) {
    return <Loading />;
  }

  const file = versionsQuery.data.dataRoomFile;

  return (
    <div className="mt-2">
      <ul role="list" className="mt-3 space-y-6">
        <Card>
          <TransitionGroup className="">
            {file.versions.map((version, i) => {
              return (
                <CSSTransition
                  key={version.id}
                  timeout={300}
                  classNames="fade-slide-down"
                  onEnter={(node: any) => node && node.offsetHeight}
                >
                  <li key={version.id}>
                    <Version
                      fileId={file.id}
                      version={version}
                      isFirst={i === 0}
                      isLast={i === file.versions.length - 1}
                      currentLiveVersionId={file.currentLiveVersion.id}
                      expandedVersionId={expandedVersion}
                      setExpandedVersion={setExpandedVersion}
                      file={file}
                      previousVersionId={
                        i < file.versions.length - 1
                          ? file.versions[i + 1].id
                          : undefined
                      }
                      onDelete={() => {
                        setDeleteVersion(version.id);
                      }}
                      onRedlineExport={(sourceId, targetId) => {
                        setRedlineExport({ sourceId, targetId });
                      }}
                    />
                  </li>
                </CSSTransition>
              );
            })}
          </TransitionGroup>
        </Card>
      </ul>
      <DetailedFileHistoryModal
        dataRoomFileId={file.id}
        open={showFullHistory}
        onClose={() => {
          setShowFullHistory(false);
        }}
      />
      <DeleteVersionModal
        open={deleteVersion !== ""}
        onClose={() => {
          setDeleteVersion("");
        }}
        dataRoomFile={file}
        versionId={deleteVersion}
      />
      <RedlineExportModal
        open={redlineExport !== null}
        onClose={() => {
          setRedlineExport(null);
        }}
        sourceVersionId={redlineExport?.sourceId || ""}
        targetVersionId={redlineExport?.targetId || ""}
        file={file}
      />
    </div>
  );
}

function Version(props: {
  fileId: string;
  version: VersionItem;
  previousVersionId?: string;
  isLast: boolean;
  isFirst: boolean;
  currentLiveVersionId: string;
  expandedVersionId: string;
  setExpandedVersion: (versionId: string) => void;
  onDelete: () => void;
  onRedlineExport: (sourceVersionId: string, targetVersionId: string) => void;
  file: VersionsFile;
}) {
  const version = props.version;
  const dispatch = useDispatch();
  const selectedFileVersionId = useSelector((state: AppState) =>
    dataRoomFileSelectors.selectedFileVersionId(state, props.fileId),
  );

  const client = useGqlClient();
  const { invalidateDataRoomFile } = useInvalidateQueryKeys();
  const updateDataRoomFile = useUpdateDataRoomFileMutation(client);

  const isSelectedVersion = selectedFileVersionId === version.id;

  return (
    <div
      className={classNames(
        " gap-x-4 transition-all duration-300 ease-in-out cursor-pointer hover:bg-gray-50 py-3",
        !props.isLast ? "border-b border-gray-100" : "",
        props.isLast ? "rounded-b-md" : "",
        props.isFirst ? "rounded-t-md" : "",
      )}
      onClick={() => {
        if (props.expandedVersionId === version.id) {
          props.setExpandedVersion("");
        } else {
          props.setExpandedVersion(version.id);
        }

        if (isSelectedVersion) {
          return;
        }

        dispatch(
          actions.setSelectedFileVersionId({
            fileId: props.fileId,
            versionId: version.id,
          }),
        );
      }}
    >
      <div>
        <div className="relative flex justify-between items-center space-y-2 px-2 ">
          <div className="flex  items-start gap-x-2 -py-0.5">
            {props.currentLiveVersionId === props.version.id ? (
              <div className="flex h-6 w-6  items-center justify-center bg-transparent">
                <div
                  className={classNames(
                    "bg-green-400/10 text-green-400 ",
                    " rounded-full p-1",
                  )}
                >
                  <div className="h-2 w-2 rounded-full bg-current" />
                </div>
              </div>
            ) : (
              <div className="flex h-6 w-6 items-center justify-center bg-transparent">
                <div
                  className={classNames(
                    "h-1.5 w-1.5 rounded-full bg-gray-300 ring-1 ring-gray-300",
                  )}
                />
              </div>
            )}

            <div className="">
              <p className="text-sm font-semibold text-gray-600">
                Version {version.versionNumber}
                <span className="ml-2 text-xs leading-2 text-gray-500 font-normal">
                  {version.changes.summary ? version.changes.summary : ""}
                </span>
              </p>

              <div className="mt-1 flex items-center  gap-x-1.5">
                <Avatar account={version.createdBy} size="xs" />
                <p className="text-xs leading-5 text-gray-500 font-light">
                  <span className="font-semibold text-xs text-gray-500">
                    {version.createdBy.name}
                  </span>{" "}
                  uploaded{" "}
                  <time
                    title={formatISO(secondsToMilliseconds(version.createdAt))}
                    dateTime={formatISO(
                      secondsToMilliseconds(version.createdAt),
                    )}
                    className="flex-none"
                  >
                    {formatDistance(
                      secondsToMilliseconds(version.createdAt),
                      new Date(),
                      {
                        addSuffix: true,
                      },
                    )}
                  </time>
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-x-2">
            <FilePill
              id={props.fileId}
              name={props.version.fileName}
              type={props.version.fileType}
              showDetailsCard={false}
            />
            <PermissionCheck
              input={{
                roleType: DealRoleType.Internal,
                permissionKey: "documents:edit",
                resource: {
                  resourceID: props.fileId,
                  resourceType: PermissionsResourceType.Document,
                },
              }}
            >
              <TripleDotMenu width="w-60">
                {[FileType.Docx, FileType.Pdf, FileType.Doc].includes(
                  props.file.fileType,
                ) && props.currentLiveVersionId !== version.id ? (
                  <MenuItem>
                    {({ focus }) => (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          updateDataRoomFile.mutate(
                            {
                              input: {
                                id: props.fileId,
                                currentLiveVersionID: version.id,
                              },
                            },
                            {
                              onSuccess: () => {
                                invalidateDataRoomFile(props.fileId);
                                toasts.success(
                                  `Version ${version.versionNumber} set as live version`,
                                );
                              },
                            },
                          );
                        }}
                        className={classNames(
                          focus ? "bg-gray-50 text-indigo-700" : "",
                          "block px-3 py-1 text-sm leading-6 text-indigo-600 cursor-pointer",
                        )}
                      >
                        Set as live version
                      </div>
                    )}
                  </MenuItem>
                ) : null}
                <FeatureFlag flag="redlining">
                  {props.file.fileType === FileType.Docx &&
                  props.file.versions.length > 1 &&
                  version.versionNumber > 1 ? (
                    <MenuItem>
                      {({ focus }) => (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            // Find the previous version to use as source
                            const previousVersionId = getPreviousVersionId(
                              props.file.versions as DataRoomFileVersion[],
                              version as DataRoomFileVersion,
                            );
                            if (previousVersionId) {
                              props.onRedlineExport(
                                previousVersionId,
                                version.id,
                              );
                            } else {
                              toasts.error(
                                "No previous version available for redline comparison",
                              );
                            }
                          }}
                          className={classNames(
                            focus ? "bg-gray-50 text-indigo-700" : "",
                            "block px-3 py-1 text-sm leading-6 text-indigo-600 cursor-pointer",
                          )}
                        >
                          Download redline
                        </div>
                      )}
                    </MenuItem>
                  ) : null}
                </FeatureFlag>
                <MenuItem>
                  {({ focus }) => (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onDelete();
                      }}
                      className={classNames(
                        focus ? "bg-gray-50 text-red-700" : "",
                        "block px-3 py-1 text-sm leading-6 text-red-600 cursor-pointer",
                      )}
                    >
                      Delete version
                    </div>
                  )}
                </MenuItem>
              </TripleDotMenu>
            </PermissionCheck>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          props.expandedVersionId === version.id
            ? "visible opacity-100 max-h-[1000px] transition-all duration-300 ease-in mt-3 overflow-y-scroll"
            : "invisible opacity-0 max-h-0 overflow-hidden transition-all duration-300 ease-out",
          "pr-3 pl-10 ",
        )}
      >
        {version.changes.textChanges.length > 0 ? (
          <div className="my-3">
            {version.changes.textChanges.map((tc) => {
              return (
                <div key={tc.section} className="mt-2">
                  <VersionTextChanges textChange={tc} />
                </div>
              );
            })}
          </div>
        ) : props.previousVersionId ? (
          <p className="text-sm font-semibold text-gray-600">
            No changes detected
          </p>
        ) : null}
      </div>

    </div>
  );
}

const versionTextChangeOptions: Option[] = [
  {
    label: "Compare",
    value: "compare",
  },
  {
    label: "New",
    value: "new",
  },
  {
    label: "Previous",
    value: "previous",
  },
];

function VersionTextChanges(props: { textChange: FileVersionTextChange }) {
  const [selectedOption, setSelectedOption] = useState<Option>(
    versionTextChangeOptions[0],
  );

  function decodeHTMLEntities(text: string): string {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = text;
    return textArea.value;
  }

  function extractLines(html: string): string[] {
    return decodeHTMLEntities(html)
      .split(/<\/?p[^>]*>/gi)
      .map((str) => str.trim())
      .filter(Boolean);
  }

  const newLines = extractLines(props.textChange.new);
  const prevLines = extractLines(props.textChange.previous);

  return (
    <div>
      <div className="bg-white rounded-md p-2 border border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <p className="font-semibold text-gray-600 text-xs">
            {props.textChange.section}
          </p>
          <Dropdown
            variant="text"
            size="xs"
            options={versionTextChangeOptions}
            selectedOption={selectedOption}
            onSelect={(o) => {
              setSelectedOption(o);
            }}
          />
        </div>
        {selectedOption.value === "new" ? (
          <p className="text-xs  text-gray-500">
            {newLines.map((line, i) => (
              <React.Fragment key={i}>
                {/* Insert a <br/> before all but the first line. */}
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </p>
        ) : null}
        {selectedOption.value === "previous" ? (
          <p className="text-xs  text-gray-500">
            {prevLines.map((line, i) => (
              <React.Fragment key={i}>
                {/* Insert a <br/> before all but the first line. */}
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </p>
        ) : null}
        {selectedOption.value === "compare" ? (
          <TextDiffs diffs={props.textChange.diffs} />
        ) : null}
      </div>
    </div>
  );
}

function getPreviousVersionId(
  versions: DataRoomFileVersion[],
  currentVersion: DataRoomFileVersion,
): string {
  // Sort versions by version number
  const sortedVersions = [...versions].sort(
    (a, b) => a.versionNumber - b.versionNumber,
  );

  // Find the index of the current version
  const currentIndex = sortedVersions.findIndex(
    (v) => v.id === currentVersion.id,
  );

  // If it's the first version or not found, there's no previous version
  if (currentIndex <= 0) {
    return ""; // Handle this case in your UI
  }

  // Return the ID of the previous version
  return sortedVersions[currentIndex - 1].id;
}
